import React from 'react';
import {
  Fade,
  Grid,
  Card,
  Box,
  Typography,
  LinearProgress,
  Avatar,
  Stack,
  Chip,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  VerifiedUser as VerifiedUserIcon,
  Link as LinkIcon,
  Assignment as AssignmentIcon,
  Groups as GroupsIcon,
  VideoCall as VideoCallIcon,
  EmojiEvents as EmojiEventsIcon,
  ArrowUpward as ArrowUpwardIcon,
} from '@mui/icons-material';
import Chart from 'react-apexcharts';

// ─── Helpers ────────────────────────────────────────────────────────────────

const pct = (v) => (typeof v === 'number' ? v.toFixed(0) : '0');

/** Gradient background style for hero cards */
const gradientBg = (from, to) => ({
  background: `linear-gradient(135deg, ${from} 0%, ${to} 100%)`,
});

// ─── Sub-components ──────────────────────────────────────────────────────────

/** Hero metric card with icon, value, subtitle and a small progress bar */
function HeroCard({ icon, iconBg, label, value, subtitle, progress, chipLabel, chipColor }) {
  return (
    <Card
      sx={{
        p: 3,
        borderRadius: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        boxShadow: '0 4px 24px 0 rgba(0,0,0,0.08)',
        position: 'relative',
        overflow: 'hidden',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: -30,
          right: -30,
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: iconBg,
          opacity: 0.08,
        },
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Avatar sx={{ bgcolor: iconBg, width: 52, height: 52 }}>{icon}</Avatar>
        {chipLabel && (
          <Chip
            label={chipLabel}
            size="small"
            color={chipColor || 'success'}
            sx={{ fontWeight: 700, fontSize: '0.7rem' }}
          />
        )}
      </Stack>
      <Box>
        <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1, color: 'text.primary', mb: 0.5 }}>
          {value}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {subtitle}
        </Typography>
      </Box>
      {typeof progress === 'number' && (
        <Box>
          <Stack direction="row" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption" color="text.secondary">Progress</Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, color: iconBg }}>{pct(progress)}%</Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={Math.min(progress, 100)}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: 'action.hover',
              '& .MuiLinearProgress-bar': { bgcolor: iconBg, borderRadius: 3 },
            }}
          />
        </Box>
      )}
    </Card>
  );
}

/** Compact inline metric row inside a card */
function MetricRow({ label, value, total, color }) {
  const ratio = total > 0 ? Math.min((value / total) * 100, 100) : 0;
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" mb={0.5}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, color }}>
          {value}<Typography component="span" variant="caption" color="text.secondary"> / {total}</Typography>
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={ratio}
        sx={{
          height: 5,
          borderRadius: 3,
          bgcolor: 'action.hover',
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 },
        }}
      />
    </Box>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

const EngagementTab = ({ data = {} }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Core metrics
  const engagementRate = data.engagementRate || 0;
  const verifiedUsers = data.verifiedUsers || 0;
  const totalUsers = data.totalUsers || 0;
  const verifiedPercentage = data.verifiedPercentage || 0;
  const totalTeachers = data.totalTeachers || 0;
  const totalEdifiers = data.totalEdifiers || 0;

  // Survey-based
  const teachersWithSessions = data.teachersWithSessions || 0;
  const edifiersWithSessions = data.edifiersWithSessions || 0;

  // Session-based
  const teachersWithSessionCount = data.teachersWithSessionCount || 0;
  const edifiersWithSessionCount = data.edifiersWithSessionCount || 0;
  const teacherSessionRate = data.teacherSessionRate || 0;
  const edifierSessionRate = data.edifierSessionRate || 0;

  // Pairing
  const pairedTeachers = data.pairedTeachers || 0;
  const pairedEdifiers = data.pairedEdifiers || 0;
  const unpairedTeachers = data.unpairedTeachers || 0;
  const unpairedEdifiers = data.unpairedEdifiers || 0;
  const pairingRate = data.pairingRate || 0;
  const totalPairs = data.totalPairs || 0;

  // Activity
  const totalSurveys = data.totalSurveys || 0;
  const totalSessions = data.totalSessions || 0;
  const totalMeetings = data.totalMeetings || 0;

  // Engagement trends
  const engagementTrends = data.engagementTrends || [];
  const trendCategories = engagementTrends.length
    ? engagementTrends.map((t) => t.week)
    : ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
  const teachersTrendData = engagementTrends.map((t) => t.teachers || 0);
  const edifiersTrendData = engagementTrends.map((t) => t.edifiers || 0);

  // ── Chart configs ──────────────────────────────────────────────────────────

  // Radial gauge for overall engagement
  const radialOptions = {
    chart: { type: 'radialBar', toolbar: { show: false } },
    plotOptions: {
      radialBar: {
        startAngle: -130,
        endAngle: 130,
        hollow: { size: '60%' },
        dataLabels: {
          name: { fontSize: '14px', fontWeight: 600, color: theme.palette.text.secondary, offsetY: 20 },
          value: {
            fontSize: '28px',
            fontWeight: 800,
            color: theme.palette.text.primary,
            offsetY: -15,
            formatter: (val) => `${val.toFixed(0)}%`,
          },
        },
        track: { background: theme.palette.action.hover, strokeWidth: '100%' },
      },
    },
    colors: [theme.palette.success.main],
    stroke: { lineCap: 'round' },
    labels: ['Engaged'],
  };

  // Role-comparison bar chart
  const roleBarOptions = {
    chart: { type: 'bar', toolbar: { show: false }, animations: { enabled: true } },
    plotOptions: {
      bar: { horizontal: true, borderRadius: 6, barHeight: '55%', distributed: false },
    },
    colors: [theme.palette.primary.main, theme.palette.success.main],
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val}%`,
      style: { fontSize: '11px', fontWeight: 700 },
    },
    xaxis: {
      categories: ['Survey Engagement', 'Session Engagement', 'Pairing Rate'],
      max: 100,
      labels: {
        formatter: (val) => `${val}%`,
        style: { fontSize: '11px', colors: theme.palette.text.secondary },
      },
    },
    yaxis: { labels: { style: { fontSize: '12px', fontWeight: 500, colors: theme.palette.text.secondary } } },
    legend: { position: 'top', fontSize: '13px', fontWeight: 600 },
    grid: { borderColor: theme.palette.divider },
    tooltip: {
      theme: isDark ? 'dark' : 'light',
      y: { formatter: (val) => `${val}%` },
    },
  };

  const teacherSurveyRate = totalTeachers > 0 ? Math.round((teachersWithSessions / totalTeachers) * 100) : 0;
  const edifierSurveyRate = totalEdifiers > 0 ? Math.round((edifiersWithSessions / totalEdifiers) * 100) : 0;
  const teacherPairingRate = totalTeachers > 0 ? Math.round((pairedTeachers / totalTeachers) * 100) : 0;
  const edifierPairingRate = totalEdifiers > 0 ? Math.round((pairedEdifiers / totalEdifiers) * 100) : 0;

  const roleBarSeries = [
    { name: 'Teachers', data: [teacherSurveyRate, teacherSessionRate, teacherPairingRate] },
    { name: 'Edifiers', data: [edifierSurveyRate, edifierSessionRate, edifierPairingRate] },
  ];

  // Role bar x-axis labels with corrected names
  roleBarOptions.xaxis.categories = ['Survey Completion', 'Session Attendance', 'Pairing Rate'];

  // Area chart for trends
  const areaOptions = {
    chart: {
      type: 'area',
      stacked: false,
      animations: { enabled: true, easing: 'easeinout', speed: 800 },
      toolbar: { show: false },
    },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: [3, 3] },
    fill: {
      type: 'gradient',
      gradient: { shadeIntensity: 1, opacityFrom: 0.45, opacityTo: 0.05, stops: [0, 90, 100] },
    },
    xaxis: {
      categories: trendCategories,
      labels: { style: { fontSize: '12px', fontWeight: 500, colors: theme.palette.text.secondary } },
    },
    yaxis: {
      title: { text: 'Active Users', style: { fontSize: '12px', fontWeight: 600, color: theme.palette.text.secondary } },
      labels: { style: { colors: theme.palette.text.secondary } },
    },
    colors: [theme.palette.primary.main, theme.palette.success.main],
    grid: { borderColor: theme.palette.divider },
    legend: { position: 'top', fontSize: '13px', fontWeight: 600 },
    tooltip: {
      shared: true,
      intersect: false,
      theme: isDark ? 'dark' : 'light',
    },
    markers: { size: 4, hover: { size: 7 } },
  };
  const areaSeries = [
    { name: 'Teachers Active', data: teachersTrendData },
    { name: 'Edifiers Active', data: edifiersTrendData },
  ];

  // Donut chart — pairing breakdown
  const donutOptions = {
    chart: { type: 'donut', toolbar: { show: false } },
    labels: ['Paired Teachers', 'Unpaired Teachers', 'Paired Edifiers', 'Unpaired Edifiers'],
    colors: [
      theme.palette.primary.main,
      theme.palette.primary.light,
      theme.palette.success.main,
      theme.palette.success.light,
    ],
    dataLabels: { enabled: false },
    legend: { position: 'bottom', fontSize: '12px', fontWeight: 500 },
    plotOptions: {
      pie: {
        donut: {
          size: '68%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total Pairs',
              fontSize: '13px',
              fontWeight: 600,
              color: theme.palette.text.secondary,
              formatter: () => totalPairs,
            },
          },
        },
      },
    },
    tooltip: { theme: isDark ? 'dark' : 'light' },
  };
  const donutSeries = [pairedTeachers, unpairedTeachers, pairedEdifiers, unpairedEdifiers];

  return (
    <Fade in timeout={500}>
      <Grid container spacing={3}>

        {/* ── Row 1: Hero KPI cards ────────────────────────────────────── */}
        <Grid item xs={12} sm={6} md={3}>
          <HeroCard
            icon={<EmojiEventsIcon sx={{ color: '#fff' }} />}
            iconBg={theme.palette.success.main}
            label="Engagement Rate"
            value={`${pct(engagementRate)}%`}
            subtitle="Users active with surveys"
            progress={engagementRate}
            chipLabel="Key Metric"
            chipColor="success"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <HeroCard
            icon={<VerifiedUserIcon sx={{ color: '#fff' }} />}
            iconBg={theme.palette.primary.main}
            label="Verified Users"
            value={verifiedUsers}
            subtitle={`${pct(verifiedPercentage)}% of ${totalUsers} total users`}
            progress={verifiedPercentage}
            chipLabel="Onboarded"
            chipColor="primary"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <HeroCard
            icon={<LinkIcon sx={{ color: '#fff' }} />}
            iconBg={theme.palette.warning.main}
            label="Pairing Rate"
            value={`${pairingRate}%`}
            subtitle={`${totalPairs} active teacher–edifier pairs`}
            progress={pairingRate}
            chipLabel="Connections"
            chipColor="warning"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <HeroCard
            icon={<VideoCallIcon sx={{ color: '#fff' }} />}
            iconBg={theme.palette.info.main}
            label="Completed Sessions"
            value={totalSessions}
            subtitle={`${totalMeetings} sessions · ${totalSurveys} surveys`}
            chipLabel="Activity"
            chipColor="info"
          />
        </Grid>

        {/* ── Row 2: Role breakdown progress + Radial gauge ────────────── */}
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3, borderRadius: 3, height: '100%', boxShadow: '0 4px 24px 0 rgba(0,0,0,0.08)' }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2.5}>
              <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 36, height: 36 }}>
                <SchoolIcon sx={{ fontSize: 20, color: '#fff' }} />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>Teachers</Typography>
                <Typography variant="caption" color="text.secondary">
                  {totalTeachers} total
                </Typography>
              </Box>
              <Box sx={{ ml: 'auto' }}>
                <Chip
                  icon={<ArrowUpwardIcon sx={{ fontSize: '14px !important' }} />}
                  label={`${teacherSurveyRate}% engaged`}
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 700 }}
                />
              </Box>
            </Stack>
            <Stack spacing={2}>
              <MetricRow label="Submitted session survey" value={teachersWithSessions} total={totalTeachers} color={theme.palette.primary.main} />
              <MetricRow label="Joined a session" value={teachersWithSessionCount} total={totalTeachers} color={theme.palette.primary.dark} />
              <MetricRow label="Paired with edifier" value={pairedTeachers} total={totalTeachers} color={theme.palette.warning.main} />
            </Stack>

            <Box sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${theme.palette.divider}` }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2.5}>
                <Avatar sx={{ bgcolor: theme.palette.success.main, width: 36, height: 36 }}>
                  <PersonIcon sx={{ fontSize: 20, color: '#fff' }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>Edifiers</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {totalEdifiers} total
                  </Typography>
                </Box>
                <Box sx={{ ml: 'auto' }}>
                  <Chip
                    icon={<ArrowUpwardIcon sx={{ fontSize: '14px !important' }} />}
                    label={`${edifierSurveyRate}% engaged`}
                    size="small"
                    color="success"
                    sx={{ fontWeight: 700 }}
                  />
                </Box>
              </Stack>
              <Stack spacing={2}>
              <MetricRow label="Submitted session survey" value={edifiersWithSessions} total={totalEdifiers} color={theme.palette.success.main} />
              <MetricRow label="Joined a session" value={edifiersWithSessionCount} total={totalEdifiers} color={theme.palette.success.dark} />
              <MetricRow label="Paired with teacher" value={pairedEdifiers} total={totalEdifiers} color={theme.palette.warning.main} />
              </Stack>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ p: 3, borderRadius: 3, height: '100%', boxShadow: '0 4px 24px 0 rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column' }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <EmojiEventsIcon sx={{ color: theme.palette.success.main }} />
              <Typography variant="subtitle1" fontWeight={700}>Overall Engagement</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" mb={1}>
              % of teachers &amp; edifiers who have participated in surveys
            </Typography>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Chart
                options={radialOptions}
                series={[Math.min(engagementRate, 100)]}
                type="radialBar"
                height={260}
              />
            </Box>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Tooltip title="Teachers with surveys">
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight={800} color="primary.main">{teachersWithSessions}</Typography>
                  <Typography variant="caption" color="text.secondary">Teachers</Typography>
                </Box>
              </Tooltip>
              <Box sx={{ width: 1, bgcolor: 'divider' }} />
              <Tooltip title="Edifiers with surveys">
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight={800} color="success.main">{edifiersWithSessions}</Typography>
                  <Typography variant="caption" color="text.secondary">Edifiers</Typography>
                </Box>
              </Tooltip>
              <Box sx={{ width: 1, bgcolor: 'divider' }} />
              <Tooltip title="Verified users">
                <Box textAlign="center">
                  <Typography variant="h6" fontWeight={800} color="info.main">{verifiedUsers}</Typography>
                  <Typography variant="caption" color="text.secondary">Verified</Typography>
                </Box>
              </Tooltip>
            </Stack>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ p: 3, borderRadius: 3, height: '100%', boxShadow: '0 4px 24px 0 rgba(0,0,0,0.08)' }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <GroupsIcon sx={{ color: theme.palette.warning.main }} />
              <Typography variant="subtitle1" fontWeight={700}>Pairing Distribution</Typography>
            </Stack>
            <Chart options={donutOptions} series={donutSeries} type="donut" height={280} />
            <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" mt={1}>
              <Chip label={`${pairedTeachers} paired teachers`} size="small" color="primary" variant="outlined" />
              <Chip label={`${pairedEdifiers} paired edifiers`} size="small" color="success" variant="outlined" />
            </Stack>
          </Card>
        </Grid>

        {/* ── Row 3: Role comparison bar chart ────────────────────────── */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 24px 0 rgba(0,0,0,0.08)' }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <AssignmentIcon sx={{ color: theme.palette.primary.main }} />
              <Typography variant="subtitle1" fontWeight={700}>Engagement Breakdown by Role</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Comparing survey, session &amp; pairing engagement rates across roles
            </Typography>
            <Chart options={roleBarOptions} series={roleBarSeries} type="bar" height={250} />
          </Card>
        </Grid>

        {/* ── Area trend chart ─────────────────────────────────────────── */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 24px 0 rgba(0,0,0,0.08)' }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <TrendingUpIcon sx={{ color: theme.palette.primary.main }} />
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>Weekly Activity Trend</Typography>
                <Typography variant="body2" color="text.secondary">
                  Users submitting surveys over the last 4 weeks
                </Typography>
              </Box>
            </Stack>
            <Chart options={areaOptions} series={areaSeries} type="area" height={250} />
          </Card>
        </Grid>

        {/* ── Row 4: Quick-stat mini cards ────────────────────────────── */}
        {[
          {
            icon: <SchoolIcon sx={{ color: '#fff', fontSize: 20 }} />,
            bg: theme.palette.primary.main,
            label: 'Teachers in Sessions',
            value: teachersWithSessionCount,
            sub: `of ${totalTeachers} teachers`,
          },
          {
            icon: <PersonIcon sx={{ color: '#fff', fontSize: 20 }} />,
            bg: theme.palette.success.main,
            label: 'Edifiers in Sessions',
            value: edifiersWithSessionCount,
            sub: `of ${totalEdifiers} edifiers`,
          },
          {
            icon: <VideoCallIcon sx={{ color: '#fff', fontSize: 20 }} />,
            bg: theme.palette.info.main,
            label: 'Sessions Completed',
            value: totalSessions,
            sub: `${totalMeetings} scheduled sessions`,
          },
          {
            icon: <LinkIcon sx={{ color: '#fff', fontSize: 20 }} />,
            bg: theme.palette.warning.main,
            label: 'Active Pairs',
            value: totalPairs,
            sub: `${pairingRate}% pairing rate`,
          },
        ].map((item) => (
          <Grid item xs={6} sm={3} key={item.label}>
            <Card
              sx={{
                p: 2,
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                boxShadow: '0 2px 12px 0 rgba(0,0,0,0.06)',
              }}
            >
              <Avatar sx={{ bgcolor: item.bg, width: 44, height: 44, flexShrink: 0 }}>{item.icon}</Avatar>
              <Box>
                <Typography variant="h5" fontWeight={800} lineHeight={1} mb={0.25}>
                  {item.value}
                </Typography>
                <Typography variant="caption" fontWeight={600} display="block">
                  {item.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {item.sub}
                </Typography>
              </Box>
            </Card>
          </Grid>
        ))}

      </Grid>
    </Fade>
  );
};

export default EngagementTab;
